<template>
  <div class="at-wrap">
    <div class="at-brand"><h1>🚑 Ambulance Tracker</h1><div class="at-sub" v-if="vehicle">{{ vehicle.hospital_name }}</div></div>
    <div v-if="error" class="at-err">{{ error }}</div>
    <div v-if="vehicle">
      <div class="at-card at-row2">
        <div><div class="lbl">Call sign</div><div class="big">{{ vehicle.call_sign || '—' }}</div></div>
        <div><div class="lbl">Registration</div><div class="val">{{ vehicle.registration_no || '—' }}</div></div>
      </div>
      <div class="at-status" :class="statusKind"><span class="dot"></span><span>{{ statusMsg }}</span></div>
      <div v-if="watching" class="at-speed"><div class="speed-num">{{ Math.round(speed) }}</div><div class="speed-unit">km/h</div></div>
      <div v-if="watching" class="at-card">
        <div class="row"><span class="k">Latitude</span><span class="v">{{ lat ? lat.toFixed(6) : '—' }}</span></div>
        <div class="row"><span class="k">Longitude</span><span class="v">{{ lng ? lng.toFixed(6) : '—' }}</span></div>
        <div class="row"><span class="k">Accuracy</span><span class="v">{{ acc ? Math.round(acc) + ' m' : '—' }}</span></div>
        <div class="row"><span class="k">Last ping</span><span class="v">{{ lastPingDisplay }}</span></div>
        <div class="row"><span class="k">Pings sent</span><span class="v">{{ pingCount }}</span></div>
      </div>
      <button v-if="!watching" class="at-btn" @click="start">▶ Start broadcasting</button>
      <button v-else class="at-btn stop" @click="stop">⏹ Stop</button>
    </div>
    <div class="at-foot">Keep this page open while on duty. GPS streams every 15 seconds.</div>
  </div>
</template>

<script>
/* eslint-disable */
import { executeJson, hms } from "@/api";
export default {
  name: "AmbulanceTrackerPublic",
  data() {
    return {
      vehicle: null, error: '',
      watching: false, watchId: null, pingTimer: null,
      lat: null, lng: null, acc: null, speed: 0,
      pingCount: 0, lastPingDisplay: '—',
      statusMsg: 'Waiting for GPS permission…', statusKind: 'wait'
    };
  },
  mounted() { this.load(); },
  beforeUnmount() { this.stop(); },
  methods: {
    async load() {
      var token = this.$route.params.token;
      if (!token || token.length !== 32) { this.error = 'Invalid tracker link.'; return; }
      try {
        var r = await executeJson({ function: 'getAmbulanceTrackerInfo', t: token }, hms);
        if (!r.data || !r.data.success) { this.error = (r.data && r.data.message) || 'Tracker not found.'; return; }
        this.vehicle = r.data.data;
      } catch (e) { this.error = 'Could not connect.'; }
    },
    start() {
      if (!navigator.geolocation) { this.setStatus('Geolocation not supported.', 'bad'); return; }
      this.watching = true;
      this.setStatus('Requesting GPS permission…', 'wait');
      this.watchId = navigator.geolocation.watchPosition(this.onFix, this.onErr, { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
      this.pingTimer = setInterval(() => this.sendPing(), 15000);
      // Wake-lock if supported
      if ('wakeLock' in navigator) navigator.wakeLock.request('screen').catch(() => {});
    },
    stop() {
      if (this.watchId !== null) { navigator.geolocation.clearWatch(this.watchId); this.watchId = null; }
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      this.watching = false;
      this.setStatus('Stopped.', 'wait');
    },
    onFix(pos) {
      this.lat = pos.coords.latitude; this.lng = pos.coords.longitude; this.acc = pos.coords.accuracy;
      this.speed = pos.coords.speed != null ? Math.max(0, pos.coords.speed * 3.6) : 0;
      this.setStatus('🟢 Tracking live — sending every 15s', 'ok');
    },
    onErr(err) {
      if (err.code === 1)      this.setStatus('Permission denied. Enable location for this page.', 'bad');
      else if (err.code === 2) this.setStatus('Position unavailable.', 'bad');
      else if (err.code === 3) this.setStatus('GPS timeout. Re-trying…', 'wait');
      else                     this.setStatus('GPS error.', 'bad');
    },
    async sendPing() {
      if (this.lat == null || this.lng == null) return;
      var params = { function: 'pingAmbulanceLocation', t: this.$route.params.token, lat: this.lat, lng: this.lng };
      if (this.acc != null)   params.accuracy_m = Math.round(this.acc);
      if (this.speed != null) params.speed_kmh  = this.speed.toFixed(1);
      try {
        var r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.pingCount++; this.lastPingDisplay = new Date().toLocaleTimeString(); }
      } catch (e) {}
    },
    setStatus(msg, kind) { this.statusMsg = msg; this.statusKind = kind; }
  }
};
</script>

<style scoped>
.at-wrap { max-width: 500px; margin: 0 auto; padding: 24px 18px 60px; background: #0a1a2f; color: #fff; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; }
.at-brand { text-align: center; margin-bottom: 24px; }
.at-brand h1 { margin: 0; font-size: 22px; font-weight: 800; }
.at-sub { font-size: 13px; color: #9bb0d0; margin-top: 4px; }
.at-card { background: #152846; border: 1px solid #2a4570; border-radius: 14px; padding: 18px; margin-bottom: 14px; }
.at-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.lbl { font-size: 11px; letter-spacing: 1.5px; color: #9bb0d0; text-transform: uppercase; margin-bottom: 4px; }
.big { font-size: 28px; font-weight: 800; color: #4FC3F7; }
.val { font-size: 16px; font-weight: 700; }
.row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; border-bottom: 1px dashed #2a4570; }
.row:last-child { border-bottom: none; }
.row .k { color: #9bb0d0; }
.row .v { font-weight: 600; }
.at-status { display: flex; gap: 10px; align-items: center; padding: 14px; border-radius: 12px; margin-bottom: 14px; font-size: 14px; }
.at-status .dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
.at-status.ok    { background: rgba(40,167,69,.15);  border: 1px solid rgba(40,167,69,.4); }
.at-status.ok .dot { background: #28a745; box-shadow: 0 0 10px #28a745; animation: pulse 2s infinite; }
.at-status.wait  { background: rgba(245,158,11,.10); border: 1px solid rgba(245,158,11,.4); }
.at-status.wait .dot { background: #f59e0b; }
.at-status.bad   { background: rgba(220,53,69,.10); border: 1px solid rgba(220,53,69,.4); }
.at-status.bad .dot { background: #dc3545; }
@keyframes pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.3);opacity:.6} }
.at-speed { background: linear-gradient(135deg, #1a3863, #224a82); border-radius: 14px; padding: 24px; text-align: center; margin-bottom: 14px; }
.speed-num { font-size: 58px; font-weight: 800; color: #4FC3F7; line-height: 1; }
.speed-unit { font-size: 12px; color: #9bb0d0; margin-top: 4px; letter-spacing: 2px; }
.at-btn { display: block; width: 100%; padding: 16px; font-size: 16px; font-weight: 700; border-radius: 12px; border: none; cursor: pointer; background: #0047AB; color: #fff; }
.at-btn.stop { background: #dc3545; }
.at-foot { text-align: center; font-size: 11px; color: #9bb0d0; margin-top: 18px; }
.at-err { background: rgba(220,53,69,.12); border: 1px solid rgba(220,53,69,.4); padding: 14px; border-radius: 10px; color: #ffd7da; }
</style>
